// FILE: pdf.tsx
import moment from 'moment';
import pdfMakeImport from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import SDLogo from "../../assets/images/logo1.png";
import { compararOrdemArquivo } from "../../services/FileService";

const pdfMake = (pdfMakeImport as any);

// Tenta garantir vfs por várias estratégias
async function ensureVfs() {
  try {
    const anyPdfMake = pdfMake as any;

    // 1) já definido
    if (anyPdfMake.vfs && Object.keys(anyPdfMake.vfs).length) return;

    // 2) do import direto do módulo vfs_fonts (possíveis formatos)
    const candidate = (pdfFonts as any).pdfMake?.vfs || (pdfFonts as any).vfs || (pdfFonts as any);
    if (candidate && Object.keys(candidate).length) {
      anyPdfMake.vfs = candidate;
      return;
    }

    // 3) window global (em bundlers diferentes)
    const win = (window as any);
    if (win.pdfMake?.vfs && Object.keys(win.pdfMake.vfs).length) {
      anyPdfMake.vfs = win.pdfMake.vfs;
      return;
    }

    // 4) tentativa dinâmica de import (fallback)
    try {
      const mod = await import(/* webpackChunkName: "pdfmake-vfs" */ 'pdfmake/build/vfs_fonts');
      const v = (mod as any).pdfMake?.vfs || (mod as any).vfs || mod;
      if (v && Object.keys(v).length) {
        anyPdfMake.vfs = v;
        return;
      }
    } catch {
      // segue para erro abaixo
    }

    // se não encontrou, lança para permitir fallback externo
    throw new Error('vfs não encontrado');
  } catch (err) {
    throw new Error('pdfMake vfs não encontrado. Falha ao carregar fontes (vfs_fonts).');
  }
}

const safeGetBase64FromURL = (url: string) =>
  new Promise<string | null>((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) return resolve(null);
          ctx.drawImage(img, 0, 0);
          const dataURL = canvas.toDataURL('image/png');
          resolve(dataURL);
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = url;
      // timeout safety
      setTimeout(() => resolve(null), 5000);
    } catch {
      resolve(null);
    }
  });

const safeGetBase64FromFile = (file: File) =>
  new Promise<string | null>((resolve) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
      // timeout fallback
      setTimeout(() => resolve(null), 5000);
    } catch {
      resolve(null);
    }
  });

// HTML -> pdfmake converter (mantido)
/** Texto puro de um bloco já convertido — usado para conferir se nada se perdeu. */
function extractText(node: any): string {
  if (node == null) return '';
  if (typeof node === 'string') return node;
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (Array.isArray(node.text)) return node.text.map(extractText).join('');
  if (typeof node.text === 'string') return node.text;
  if (Array.isArray(node.ol)) return node.ol.map(extractText).join('');
  if (Array.isArray(node.ul)) return node.ul.map(extractText).join('');
  return '';
}

/**
 * O relato tem conteúdo de verdade?
 *
 * ⚠️ `if (texto)` não serve: o editor grava `<p><br></p>` quando a pessoa clica no campo
 * e sai sem escrever. A string é "cheia" para o JavaScript e VAZIA para quem lê — e o
 * laudo saía com o título da seção e nada embaixo, o que num documento que vai à
 * seguradora se lê como conteúdo faltando (ou como a seção anterior repetida).
 */
function temConteudo(html?: string): boolean {
  if (!html) return false;
  try {
    const texto = new DOMParser().parseFromString(html, 'text/html').body.textContent || '';
    // `<img>` sozinho não tem texto e ainda assim é conteúdo.
    return texto.trim().length > 0 || /<img/i.test(html);
  } catch {
    return html.replace(/<[^>]*>/g, '').trim().length > 0;
  }
}

/**
 * Converte o HTML do editor em blocos do pdfmake.
 *
 * REDE DE SEGURANÇA: depois de converter, compara o que saiu com o texto que o
 * HTML realmente tem. Se sumiu conteúdo, devolve o texto puro em vez do bloco
 * formatado. O relatório pode sair sem negrito; não pode sair sem o trabalho do
 * sindicante — e o defeito que motivou isto cortava o relato no meio da frase
 * sem avisar ninguém (a tela mostrava tudo, o PDF não).
 */
function convertHtmlToPdf(htmlContent: string) {
  const content: any[] = [];
  ParseHtml(content, htmlContent || '');

  try {
    const esperado = new DOMParser()
      .parseFromString(htmlContent || '', 'text/html')
      .body.textContent?.replace(/\s+/g, ' ').trim() || '';
    const obtido = extractText(content).replace(/\s+/g, ' ').trim();
    // Margem de 5%: espaços entre tags e quebras normalizadas dançam um pouco.
    if (esperado.length > 0 && obtido.length < esperado.length * 0.95) {
      return [{ text: esperado, fontSize: 10 }];
    }
  } catch {
    /* sem DOMParser não dá para conferir — segue com o que foi convertido */
  }

  return content;
}
function ParseContainer(cnt: any[], e: any, styles: string[]) {
  const children = e.childNodes;
  if (children.length !== 0) {
    for (let i = 0; i < children.length; i++) {
      ParseElement(cnt, children[i], styles);
    }
  }
}
/** `rgb(0, 0, 0)` → `#000000`. O pdfmake não entende a notação funcional do CSS. */
function toPdfColor(value: string): string | undefined {
  const v = (value || '').trim();
  const rgb = v.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgb) {
    const hex = [rgb[1], rgb[2], rgb[3]]
      .map(n => Math.max(0, Math.min(255, parseInt(n, 10))).toString(16).padStart(2, '0'))
      .join('');
    return `#${hex}`;
  }
  if (/^#[0-9a-f]{3,8}$/i.test(v)) return v;
  if (/^[a-z]+$/i.test(v)) return v;   // nome de cor CSS
  return undefined;
}

/**
 * Estilo declarado no PRÓPRIO elemento (`<span style="...">`).
 *
 * Antes, `styles` só carregava o que vinha de `<strong>`/`<u>`/`<i>`; o atributo
 * `style` nunca era lido, então `<p style="font-weight:bold">` perdia o negrito.
 */
function ownStyles(e: any): string[] {
  const attr = typeof e?.getAttribute === 'function' ? e.getAttribute('style') : null;
  return attr ? attr.split(';').filter((x: string) => x.trim()) : [];
}

function ComputeStyle(o: any, styles: string[]) {
  for (let i = 0; i < styles.length; i++) {
    const st = styles[i].trim().toLowerCase().split(":");
    if (st.length === 2) {
      switch (st[0]) {
        case "font-size":
          o.fontSize = parseInt(st[1]);
          break;
        case "font-weight":
          if (st[1] === "bold") o.bold = true;
          break;
        case "text-decoration":
          if (st[1] === "underline") o.decoration = "underline";
          break;
        case "font-style":
          if (st[1] === "italic") o.italics = true;
          break;
        case "font-family":
          o.font = st[1];
          break;
        case "color":
          // O pdfmake aceita hex e nome de cor; rgb() precisa ser convertido.
          o.color = toPdfColor(st.slice(1).join(":"));
          break;
      }
    }
  }
}
function ParseElement(cnt: any[], e: any, styles: string[]) {
  if (!styles) styles = [];
  // Estilo herdado do pai + o declarado no proprio elemento (o `style` do no
  // nunca era lido, entao cor, tamanho e negrito inline se perdiam).
  const herdado = styles.concat(ownStyles(e));

  switch (e.nodeName.toLowerCase()) {
    case "#text":
      const t = { text: e.textContent?.replace(/\n/g, "") || "" };
      ComputeStyle(t, herdado);
      cnt.push(t);
      break;
    case "strong":
      ParseContainer(cnt, e, herdado.concat(["font-weight:bold"]));
      break;
    case "u":
      ParseContainer(cnt, e, herdado.concat(["text-decoration:underline"]));
      break;
    case "i":
      ParseContainer(cnt, e, herdado.concat(["font-style:italic"]));
      break;
    case "div":
    case "p":
      const p = { text: [] as any[] };
      ComputeStyle(p, herdado);
      // Repassa o estilo do paragrafo aos filhos: o <span> interno herda cor/fonte.
      ParseContainer(p.text, e, herdado);
      cnt.push(p);
      break;
    case "a":
      const link = { text: e.textContent || "", link: e.getAttribute("href") };
      ComputeStyle(link, herdado);
      cnt.push(link);
      break;
    case "br":
      cnt.push("\n");
      break;
    case "h1":
      const h1 = { text: e.textContent || "", fontSize: 24, bold: true };
      ComputeStyle(h1, herdado);
      cnt.push(h1);
      break;
    case "h2":
      const h2 = { text: e.textContent || "", fontSize: 20, bold: true };
      ComputeStyle(h2, herdado);
      cnt.push(h2);
      break;
    case "h3":
      const h3 = { text: e.textContent || "", fontSize: 18, bold: true };
      ComputeStyle(h3, herdado);
      cnt.push(h3);
      break;
    case "ol":
      const ol = { ol: [] as any[] };
      ParseContainer(ol.ol, e, herdado);
      cnt.push(ol);
      break;
    case "ul":
      const ul = { ul: [] as any[] };
      ParseContainer(ul.ul, e, herdado);
      cnt.push(ul);
      break;
    case "li":
      const li = { text: [] as any[] };
      ComputeStyle(li, herdado);
      ParseContainer(li.text, e, herdado);
      cnt.push(li);
      break;
    // ── Tags que o editor (react-quill) e o texto colado do Word produzem ──
    case "b":
      ParseContainer(cnt, e, herdado.concat(["font-weight:bold"]));
      break;
    case "em":
      // O Quill marca itálico com <em>; antes só <i> era reconhecido.
      ParseContainer(cnt, e, herdado.concat(["font-style:italic"]));
      break;
    case "s":
    case "strike":
    case "del": {
      const riscado: any[] = [];
      ParseContainer(riscado, e, herdado);
      riscado.forEach(n => { if (n && typeof n === 'object') n.decoration = 'lineThrough'; });
      cnt.push(...riscado);
      break;
    }
    case "h4":
    case "h5":
    case "h6": {
      const h = { text: e.textContent || "", fontSize: 14, bold: true };
      ComputeStyle(h, herdado);
      cnt.push(h);
      break;
    }
    case "blockquote": {
      const bq = { text: [] as any[], margin: [12, 4, 0, 4] as any, italics: true };
      ParseContainer(bq.text, e, herdado);
      cnt.push(bq);
      break;
    }

    /**
     * QUALQUER outra tag (span, font, sub, sup, table, section...).
     *
     * Este `default` ignorava a tag E TUDO dentro dela. Como o editor embrulha
     * em `<span style="...">` todo trecho colado ou colorido, o relato era
     * cortado no meio da frase no PDF — enquanto a tela, que injeta o HTML
     * direto, mostrava o texto inteiro. Agora a tag desconhecida é apenas
     * transparente: o conteúdo continua, com o estilo dela quando houver.
     */
    default:
      ParseContainer(cnt, e, herdado);
      break;
  }
}
function ParseHtml(cnt: any[], htmlText: string) {
  try {
    const parser = new DOMParser();
    const html = parser.parseFromString(htmlText || '', "text/html");
    for (let i = 0; i < html.body.childNodes.length; i++) {
      ParseElement(cnt, html.body.childNodes[i], []);
    }
  } catch {
    // se falhar, insere texto cru
    cnt.push({ text: htmlText || '', fontSize: 10 });
  }
}

async function SectionPhotosOs(fileKey: string, photos: any[] | undefined) {
  try {
    const items = photos
      ? photos
        .filter((i: any) => i.etapa === fileKey)
        .filter((i: any) =>
          ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'webp'].some((ext) =>
            i.url.toLowerCase().includes(ext)
          )
        )
        // Mesma ordenação da tela de relato — o PDF precisa sair na mesma sequência.
        .sort(compararOrdemArquivo)
      : [];

    const promises = items.map(async (item: any) => {
      // tenta converter; se falhar retorna null para ignorar
      const url = await safeGetBase64FromURL(`${item.url}`).catch(() => null);
      if (!url) return null;
      return [
        {
          image: url,
          width: 400,
          margin: [0, 10],
          alignment: 'center',
        },
        {
          text: item.label || '',
          alignment: 'center'
        }
      ];
    });

    const results = await Promise.all(promises);
    return (results.filter(Boolean) as any[]).flat();
  } catch {
    return [];
  }
}

async function fallbackWithJsPdf(data: any, titles: any, createdAt: string) {
  try {
    const mod = await import('jspdf');
    const { jsPDF } = mod as any;
    const doc = new jsPDF();
    const lines: string[] = [];
    lines.push(`Relatório de sindicância - ${data?.sinistro ?? ''}/${createdAt}`);
    // adicionar algumas seções principais
    if (titles?.registro) lines.push(`\n${titles.registro}`);
    if (data?.seguradora) lines.push(`Seguradora: ${data?.seguradora}`);
    if (data?.sinistro) lines.push(`Sinistro: ${data?.sinistro}`);
    if (data?.segurado) lines.push(`Segurado: ${data?.segurado}`);
    if (data?.natureza) lines.push(`Natureza: ${data?.natureza}`);
    if (data?.endereco) lines.push(`Local: ${data?.endereco}`);
    if (data?.veiculo) lines.push(`Veículo: ${data?.veiculo}`);
    if (data?.placa) lines.push(`Placa: ${data?.placa}`);
    if (data?.valorProgresso !== undefined && data?.valorProgresso !== null) lines.push(`Progresso: ${Number(data?.valorProgresso).toFixed(2)}%`);
    if (data?.regularidade) lines.push(`Regularidade: ${data?.regularidade}`);
    if (data?.tipoSindicancia) lines.push(`Tipo de sindicância: ${data?.tipoSindicancia}`);
    if (data?.registro_observacao) {
      const txt = (new DOMParser()).parseFromString(data.registro_observacao || '', 'text/html').body.textContent || '';
      lines.push(`\n${txt}`);
    }
    // escreve no PDF com quebra simples
    const pageWidth = (doc as any).internal.pageSize.getWidth();
    const margin = 10;
    const maxLineWidth = pageWidth - margin * 2;
    let y = 15;
    doc.setFontSize(12);
    lines.forEach((ln) => {
      const split = (doc as any).splitTextToSize(ln, maxLineWidth);
      (doc as any).text(split, margin, y);
      y += split.length * 7;
      if (y > (doc as any).internal.pageSize.getHeight() - 20) {
        doc.addPage();
        y = 15;
      }
    });
    const fileName = `Relatório de sindicância - ${data?.sindicancia ?? data?.sinistro ?? 'relatorio'}-${createdAt}.pdf`;
    doc.save(fileName);
    return true;
  } catch (err) {
    console.error('fallback jsPDF falhou', err);
    return false;
  }
}

export default async function docPDF(data: any, titles: any, logo: File | undefined, includes: any, photos: any[]) {
  const createdAt = `${new Date(data?.createdAt ?? Date.now()).getFullYear()}`;

  // tenta garantir vfs e gerar com pdfMake
  try {
    await ensureVfs();

    // logo
    const logoBase64 = logo ? await safeGetBase64FromFile(logo).catch(() => null) : await safeGetBase64FromURL(SDLogo).catch(() => null);

    const reportHeader: any = [
      {
        margin: 10,
        columns: [
          {
            margin: [0, 10, 0, 0],
            text: `Relatório de sindicância - ${data?.sinistro ?? ''}/${createdAt}`,
            fontSize: 16,
            bold: true,
            alignment: 'center'
          },
          logoBase64 ? {
            image: logoBase64,
            width: 100,
            alignment: 'right'
          } : {}
        ]
      }
    ];

    const details: any[] = [];
    details.push(...reportHeader);

    // helper para adicionar linha "Rótulo: valor" (igual à tela)
    const pushField = (label: string, value: any) => {
      if (value === undefined || value === null || value === '') return;
      details.push({ text: [{ text: `${label}: `, bold: true }, `${value}`], fontSize: 12, margin: [0, 5] });
    };

    // registro (mesmos campos exibidos na tela)
    if (includes?.registro) {
      details.push({ margin: [0, 10], text: titles.registro, bold: true, fontSize: 16 });
      pushField('Seguradora', data?.seguradora);
      pushField('Sinistro', data?.sinistro);
      pushField('Segurado', data?.segurado);
      pushField('Natureza', data?.natureza);
      pushField('Local', data?.endereco);
      pushField('Veículo', data?.veiculo);
      pushField('Placa', data?.placa);
      if (data?.valorProgresso !== undefined && data?.valorProgresso !== null) {
        pushField('Progresso', `${Number(data?.valorProgresso).toFixed(2)}%`);
      }
      pushField('Regularidade', data?.regularidade);
      // Caracteriza a O.S., não a entrevista: estava saindo no meio da
      // etapa Entrevista, onde não tem relação com o conteúdo ao redor.
      pushField('Tipo de sindicância', data?.tipoSindicancia);
      if (data?.registro_observacao) details.push(...convertHtmlToPdf(data?.registro_observacao));
      const photosBlock = await SectionPhotosOs('registro', photos).catch(() => []);
      if (photosBlock && photosBlock.length) details.push(...photosBlock);
    }

    // outras seções simplificadas
    if (includes?.relatoCroqui) {
      details.push({ margin: [0, 10], text: titles.relatoCroqui, bold: true, fontSize: 16 });
      if (data?.relatoCroqui) details.push(...convertHtmlToPdf(data?.relatoCroqui)); else details.push({ text: 'Não Consta' });
      const photosBlock = await SectionPhotosOs('croqui', photos).catch(() => []);
      if (photosBlock && photosBlock.length) details.push(...photosBlock);
    }

    if (includes?.relatoOrgaos) {
      details.push({ margin: [0, 10], text: titles.relatoOrgaos, bold: true, fontSize: 16 });
      if (data?.relatoOrgaos) details.push(...convertHtmlToPdf(data?.relatoOrgaos)); else details.push({ text: 'Não Consta' });
      const photosBlock = await SectionPhotosOs('orgaos-publicos', photos).catch(() => []);
      if (photosBlock && photosBlock.length) details.push(...photosBlock);
    }

    if (includes?.entrevista) {
      details.push({ margin: [0, 10], text: titles.entrevista, bold: true, fontSize: 16 });
      // campos da entrevista (mesmos exibidos na tela)
      pushField('Profissão do segurado', data?.profissaoSegurado);
      pushField('Local do pernoite', data?.localPernoite);
      pushField('Segurado é único condutor', data?.unicoCondutor);
      pushField('Residência possui pessoas entre 18 e 25 anos', data?.residIdade);
      pushField('Residência possui pessoas entre 18 e 25 anos que utilizam o veículo', data?.residIdadeVeiculo);
      pushField('Condutor principal', data?.condutorPrincipal);
      pushField('CPF do condutor cadastrado em aplicativo', data?.possuiCadastroAplicativo);
      pushField('Segurado é motorista de aplicativo', data?.cadastroAplicativo);
      pushField('No dia do evento estava com passageiros no veículo', data?.haviaPassageiro);
      pushField('Houve endosso de uso particular para comercial', data?.endossoParticularComercial);
      pushField('Evento antes ou depois do endosso', data?.endossoAntesDepois);
      pushField('Extrato das corridas corresponde ao período de vigência da apólice', data?.extratoCorrida);
      pushField('Média de Km rodado por dia/semana', data?.mediaKmDiaSemana);
      pushField('Quilometragem atual do veículo', data?.kmAtual);

      // sub-seções de convívio / transcrição (mesmas da tela)
      const subSections: { key: string; text: any }[] = [
        { key: 'segConvivioComercialRel', text: data?.segConvivioComercialRel },
        { key: 'segConvivioResidencialRel', text: data?.segConvivioResidencialRel },
        { key: 'terConvivioComercialRel', text: data?.terConvivioComercialRel },
        { key: 'terConvivioResidencialRel', text: data?.terConvivioResidencialRel },
        { key: 'transcricaoEntrevista', text: data?.transcricaoEntrevista },
      ];
      /*
       * ⚠️ `includes[key]` é metade da condição, e era a metade que faltava: estas cinco
       * sub-seções ignoravam o "Incluir na exportação" da tela. Desmarcar "Entrevista com
       * o Terceiro" não tinha efeito nenhum no PDF — a única forma de tirar o terceiro era
       * desmarcar a seção Entrevistas inteira, levando junto a entrevista do segurado.
       * As seções de primeiro nível sempre respeitaram; só as aninhadas ficaram de fora.
       */
      subSections.forEach(({ key, text }) => {
        if (includes?.[key] !== false && temConteudo(text)) {
          details.push({ margin: [0, 8], text: titles[key], bold: true, fontSize: 14 });
          details.push(...convertHtmlToPdf(text));
        }
      });

      const photosBlock = await SectionPhotosOs('entrevista', photos).catch(() => []);
      if (photosBlock && photosBlock.length) details.push(...photosBlock);
    }

    if (includes?.redesSociais) {
      details.push({ margin: [0, 10], text: titles.redesSociais, bold: true, fontSize: 16 });
      if (data?.redesSociais?.relato) details.push(...convertHtmlToPdf(data?.redesSociais?.relato)); else details.push({ text: 'Não Consta' });
      const photosBlock = await SectionPhotosOs('redes-sociais', photos).catch(() => []);
      if (photosBlock && photosBlock.length) details.push(...photosBlock);
    }

    if (includes?.conclusao) {
      details.push({ margin: [0, 10], text: titles.conclusao, bold: true, fontSize: 16 });
      // A causa acompanha a conclusão (mesma etapa) e antes não saía no PDF.
      if (data?.causa) {
        details.push({ margin: [0, 4, 0, 2], text: 'Causa', bold: true, fontSize: 12 });
        details.push(...convertHtmlToPdf(data?.causa));
      }
      if (data?.conclusao) details.push(...convertHtmlToPdf(data?.conclusao)); else details.push({ text: 'Não Consta' });
      const photosBlock = await SectionPhotosOs('conclusao', photos).catch(() => []);
      if (photosBlock && photosBlock.length) details.push(...photosBlock);
    }

    const docDefinitions: TDocumentDefinitions = {
      pageSize: 'A4',
      pageMargins: [38, 15, 36, 38],
      content: details,
      footer: function (currentPage, pageCount) {
        return [
          {
            text: 'Direitos Reservados © | Sindicante Digital LTDA – CNPJ: 41.358.010/0001-80',
            fontSize: 10,
            alignment: 'center',
            color: '#696969',
          },
          {
            fontSize: 9,
            text: [
              {
                margin: [0, 20, 0, 38],
                text: currentPage.toString() + ' de ' + pageCount,
              }
            ],
            alignment: 'center'
          }
        ];
      },
    };

    /*
     * ⚠️ `??` só descarta null/undefined, e o nº da sindicância costuma vir `"0"` — que é
     * string cheia. O arquivo chegava como "Relatório de sindicância - 0-2026.pdf" para
     * toda O.S., e quem recebe dezenas por semana não tem como arquivar.
     */
    const identificador = [data?.sindicancia, data?.sinistro]
      .map((v: any) => String(v ?? '').trim())
      .find((v: string) => v && v !== '0') || 'relatorio';
    (pdfMake as any).createPdf(docDefinitions).download(`Relatório de sindicância - ${identificador}-${createdAt}.pdf`);
    return;
  } catch (err) {
    // se falhar por vfs ou outro motivo, tenta fallback com jsPDF para garantir export
    console.warn('pdfMake falhou — tentando fallback jsPDF:', err);
    const ok = await fallbackWithJsPdf(data, titles, createdAt);
    if (!ok) throw err; // rethrow se fallback também falhar
  }
}